import {
  Box,
  CircularProgress,
  useTheme,
  Chip,
  IconButton,
  Collapse,
  CardContent,
  CardActions,
  Card,
  CardHeader,
  Divider,
  Tooltip,
  Switch,
  Tabs,
  Tab,
} from "@mui/material";
import AppDropDown from "../../components/common/filter/AppDropDown";
import { FilterValue } from "../../../models/Filters/FilterValue";
import FormSmallTextField from "../../components/common/form/FormTextField";
import AppSearchDropDown from "../../components/common/filter/AppSearchDropDown";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddProductVariants from "../../components/product/AddProductVariants";
import "../../../styles/product.css";
import { useStores } from "../../../use-stores";
import { useLocation, useNavigate } from "react-router-dom";
import { useNotification } from "../../../utils/NotificationsContext";
import { observer } from "mobx-react";
import MediaAssetsUpload from "../../components/common/upload/MediaAssetsUpload";
import { ImageData } from "../../../models/Image/Image";
import {
  ProductImage,
  ProductCategoriesMapping,
} from "../../../models/Products/Product";
import ImageAPI from "../../../api/ImageAPI";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import RichTextEditor from "../../components/common/textEditor/RichTextEditor";
import { ExpandLess, ExpandMore, Info } from "@mui/icons-material";
import HistoryIcon from "@mui/icons-material/History";
import SearchIcon from "../../../assets/searchIcon.png";
import NotesIcon from "../../../assets/notesIcon.png";
import SourcingIcon from "../../../assets/sourcingIcon.png";
import BusinessIcon from "../../../assets/business_building.png";
import TagIcon from "../../../assets/hashtagIcon.png";
import EyeIcon from "../../../assets/eyeframe.png";
import PercentIcon from "../../../assets/percentIcon.png";
import FlashOnIcon from "../../../assets/flash0nIcon.png";
import StatusIcon from "../../../assets/pricetagicon.png";
import ProductIcon from "../../../assets/productIcon.png";
import RupeeIcon from "../../../assets/rupeeicon.png";
import ColorPadIcon from "../../../assets/colorpadicon.png";
import SkuIcon from "../../../assets/liningicon.png";
import UploadIcon from "../../../assets/uploadimg.png";
import ImageIcon from "../../../assets/imageicon.png";
import CustomTypography from "../../components/common/form/CustomTypography";
import AppButton from "../../components/common/buttons/AppButton";
import FormTextField from "../../components/common/form/FormTextField";
import GradientDivider from "../../components/common/divider/GradientDivider";
import FileUploadIcon from "../../../assets/fileuploadicon.png";
import {
  formatAmount,
  formatDate,
} from "../../components/common/functions/formatDate";
import ActivityLogDrawer from "../../components/common/drawer/ActivityLogDrawer";
import PodProductPsdMappingsCard from "../../components/product/PodProductPsdMappingsCard";
import MockupConfigAPI from "../../../api/MockupConfigAPI";

const AddProduct: React.FC = () => {
  const activeFilters: FilterValue[] = [
    { id: "-1", value: "Select" },
    { id: "true", value: "Active" },
    { id: "false", value: "Inactive" },
  ];

  const gstFilters: FilterValue[] = [
    { id: "-1", value: "Select *" },
    { id: "0", value: "0" },
    { id: "3", value: "3" },
    { id: "5", value: "5" },
    { id: "18", value: "18" },
    { id: "28", value: "28" },
    { id: "40", value: "40" },
  ];
  const theme = useTheme();
  const { productStore, userStore, vendorStore, podCatalogStore } = useStores();
  const location = useLocation();
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotification();
  const queryParams = new URLSearchParams(location.search);
  const productId = queryParams.get("product-id") || null;
  const [categoryFilters, setCategoryFilters] = useState<FilterValue[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [, setSearchTrigger] = useState<number>(0);
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [purchaseInsightsOpen, setPurchaseInsightsOpen] = useState(true);
  const [isActivityDrawerOpen, setIsActivityDrawerOpen] = useState(false);
  const [isActivityLoading, setIsActivityLoading] = useState(false);
  const [addToPodLoading, setAddToPodLoading] = useState(false);
  const [psdLoadingOptions, setPsdLoadingOptions] = useState(false);
  const [psdUploading, setPsdUploading] = useState(false);
  const mockupConfigAPI = useMemo(() => new MockupConfigAPI(), []);
  const isInPodCatalog = Boolean(
    productId && productStore.productDetails.in_pod_catalog,
  );

  const inputVendorNameTextRef = useRef<string>("");
  let vendorTimer: string | number | NodeJS.Timeout | null | undefined = null;

  const isViewOnly = (): boolean => {
    return !userStore.hasPermission(EmployeeRoles.EDIT_PRODUCT);
  };

  const canChangeCostPrice = (): boolean => {
    return userStore.hasPermission(EmployeeRoles.CHANGE_COST_PRICE);
  };

  const canChangeSellingPrice = (): boolean => {
    return userStore.hasPermission(EmployeeRoles.CHANGE_SELLING_PRICE);
  };

  const canChangeZohoId = () => {
    const hasPermission = userStore.hasPermission(
      EmployeeRoles.CREATE_INVOICE_AND_PI,
    );
    const isZohoIdEmpty = !productStore.originalProduct?.zoho_id;
    return hasPermission && isZohoIdEmpty;
  };

  const handleError = (message?: string) => {
    showError(
      "Error",
      message || "Something went wrong! Please try again later.",
    );
  };

  const setPreferredVendorId = (preferredVendorId: number | null) => {
    const storeWithOptionalUpdater = productStore as typeof productStore & {
      updatePreferredVendorId?: (id: number | null) => void;
    };
    if (
      typeof storeWithOptionalUpdater.updatePreferredVendorId === "function"
    ) {
      storeWithOptionalUpdater.updatePreferredVendorId(preferredVendorId);
      return;
    }
    productStore.productDetails.preferred_vendor_id = preferredVendorId;
  };

  useEffect(() => {
    setIsLoading(true);
    if (productId) {
      productStore
        .getProductDetailsById(Number(productId))
        .then(() => {
          productStore
            .getPrintingTypes()
            .catch((err) => handleError(err?.response?.data?.message));
        })
        .catch((err) => handleError(err?.response?.data?.message))
        .finally(() => setIsLoading(false));

      productStore
        .fetchPurchaseRequestInsights(Number(productId))
        .catch((err) => console.log(err?.response?.data?.message));
    } else {
      productStore.clearProductDetails();
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, productStore]);

  useEffect(() => {
    setIsLoading(true);
    const getProductOptions = async () => {
      try {
        if (productStore.productOptions.length === 0) {
          await productStore.getProductOptions();
        }
      } catch (err) {}
    };
    const getCategories = async () => {
      setIsLoading(true);
      try {
        if (productStore.categories.length === 0) {
          await productStore.getCategories();
        }

        const filterValues: FilterValue[] = productStore.categories.map(
          (category) => ({
            id: String(category.id),
            value: category.name,
          }),
        );

        setCategoryFilters(filterValues);
      } catch (err) {
        showError(
          "Error fetching categories",
          "An error occurred while fetching categories.",
        );
      } finally {
        setIsLoading(false);
      }
    };

    getProductOptions();
    getCategories();
  }, [productStore, showError]);

  const searchVendor = (name: string) => {
    inputVendorNameTextRef.current = name;
    setSearchTrigger((prev) => prev + 1);
    if (vendorTimer) {
      clearTimeout(vendorTimer);
    }

    if (name.length > 2) {
      vendorTimer = setTimeout(() => {
        vendorStore
          .getVendors({ page_no: 1, search_text: name })
          .catch((err) =>
            showError(
              "Error occurred",
              err?.response?.data?.message ||
                "Something went wrong! Please try again later.",
            ),
          );
      }, 700);
    }
  };

  const saveDetails = () => {
    const { name, desc, cost_price, selling_price, active } =
      productStore.productDetails;

    if (!name) {
      showError("Error occurred", "Product name is required.");
      return;
    }

    if (!desc) {
      showError("Error occurred", "Product description is required.");
      return;
    }

    if (cost_price && isNaN(cost_price)) {
      showError("Error occurred", "Cost price must be a valid number.");
      return;
    }

    if (!selling_price) {
      showError("Error occurred", "Selling price is required.");
      return;
    }

    if (isNaN(selling_price)) {
      showError("Error occurred", "Selling price must be a valid number.");
      return;
    }

    if (active === undefined) {
      showError("Error occurred", "Active field is required.");
      return;
    }

    const variants = productStore.productDetails.variants;
    if (!variants || variants.length === 0)
      return showError(
        "Validation Failed",
        "Product cannot be added/updated without a size variant.",
      );

    const size_variants = variants.filter(
      (variant) => variant.options_id === 1,
    );
    if (size_variants.length === 0)
      return showError(
        "Validation Failed",
        "Product cannot be added/updated without a size variant.",
      );

    setIsLoading(true);
    if (productId) {
      updateProduct();
    } else {
      createProduct();
    }
  };

  const updateProduct = () => {
    productStore
      .updateProductDetails()
      .then(() => {
        setIsLoading(false);
        showSuccess("Product updated successfully");
      })
      .catch((err) => {
        showError(
          "Error occurred",
          err?.response?.data?.message ||
            "Something went wrong! Please try again later.",
        );
      })
      .finally(() => setIsLoading(false));
  };

  const saveSEODetails = () => {
    const { seo_title, seo_description } = productStore.productDetails;

    if (!seo_title || seo_title.trim() === "") {
      showError("Error occurred", "SEO title is required.");
      return;
    }

    if (!seo_description || seo_description.trim() === "") {
      showError("Error occurred", "SEO description is required.");
      return;
    }

    setIsLoading(true);
    productStore
      .updateProductSEO()
      .then(() => {
        showSuccess("SEO details saved successfully");
      })
      .catch((err: any) => {
        showError(
          "Error occurred",
          err?.response?.data?.message ||
            err?.message ||
            "Something went wrong! Please try again later.",
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const saveSourcingDetails = () => {
    const { hsn_code, gst_percentage } = productStore.productDetails;

    if (!hsn_code) {
      showError("Error occurred", "HSN code is required.");
      return;
    }

    if (isNaN(Number(hsn_code))) {
      showError("Error occurred", "HSN code must be a valid number.");
      return;
    }

    if (hsn_code.length !== 6 && hsn_code.length !== 8) {
      showError("Error occurred", "HSN code must be 6 or 8 digits long.");
      return;
    }

    if (hsn_code === "" || hsn_code === "000000" || hsn_code === "00000000") {
      showError("Error occurred", "Invalid HSN code.");
      return;
    }

    if (hsn_code === "123456" || hsn_code === "12345678") {
      showError("Error occurred", "Invalid HSN code.");
      return;
    }

    if (hsn_code === "654321" || hsn_code === "87654321") {
      showError("Error occurred", "Invalid HSN code.");
      return;
    }

    // Check for repeating same digits (e.g., 1111, 22222, 333333, etc.)
    const hasRepeatingDigits = /^(\d)\1+$/.test(hsn_code);
    if (hasRepeatingDigits) {
      showError(
        "Error occurred",
        "HSN code cannot contain only repeating same digits.",
      );
      return;
    }

    if (
      gst_percentage === undefined ||
      gst_percentage === -1 ||
      gst_percentage === null
    ) {
      showError("Error occurred", "GST percentage is required.");
      return;
    }

    setIsLoading(true);
    productStore
      .updateProductSource()
      .then(() => {
        showSuccess("Sourcing details saved successfully");
      })
      .catch((err: any) => {
        showError(
          "Error occurred",
          err?.response?.data?.message ||
            err?.message ||
            "Something went wrong! Please try again later.",
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const createProduct = () => {
    setIsLoading(true);
    if (!productStore.productDetails.name) {
      showError("Error occurred", "Product name is required.");
      return;
    }
    if (!productStore.productDetails.desc) {
      showError("Error occurred", "Product description is required.");
      return;
    }
    if (!productStore.productDetails.cost_price) {
      showError("Error occurred", "Cost price is required.");
      return;
    }
    if (!productStore.productDetails.selling_price) {
      showError("Error occurred", "Selling price is required.");
      return;
    }
    if (!productStore.productDetails.active) {
      showError("Error occurred", "Active field is required.");
      return;
    }
    if (
      !productStore.productDetails.categories ||
      productStore.productDetails.categories.length === 0
    ) {
      showError("Error occurred", "At least one category is required.");
      return;
    }

    productStore
      .createProduct({
        ...productStore.productDetails,
        image_urls: undefined,
      })
      .then((res) => {
        showSuccess("Product created successfully");
        productStore.reset();
        navigate(`/products/add-edit-product?product-id=${res}`);
      })
      .catch((err) => {
        showError(
          "Error occurred",
          err?.response?.data?.message ||
            "Something went wrong! Please try again later.",
        );
      })
      .finally(() => setIsLoading(false));
  };

  const resetProductSection = () => {
    const originalProduct = productStore.originalProduct;
    productStore.updateName(originalProduct.name || "");
    productStore.updateDescription(originalProduct.desc || "");
    productStore.updateCostPrice(originalProduct.cost_price || 0);
    productStore.updateSellingPrice(originalProduct.selling_price || 0);
    productStore.updateCategory(
      originalProduct.categories?.map((category) => ({
        id: String(category.category_id),
        value: category.category_name || "",
      })) ||
        (originalProduct.category_id
          ? [
              {
                id: String(originalProduct.category_id),
                value: originalProduct.category_name || "",
              },
            ]
          : []),
    );
    productStore.updateProductStatus(originalProduct.active || false);
    productStore.updateDisplay(originalProduct.display || false);
    productStore.updateSKU(originalProduct.sku || "");

    productStore.productDetails.image_urls = [
      ...(originalProduct.image_urls || []),
    ];
    productStore.productDetails.size_chart_url = originalProduct.size_chart_url;

    productStore.productDetails.variants = [
      ...(originalProduct.variants || []),
    ];
    setSelectedImages([]);
    // setSelectedProductImage(undefined);
    productStore.clearSelectedSizeChartFile();
  };

  const resetSourcingSection = () => {
    const originalProduct = productStore.originalProduct;
    productStore.updatedVendors(
      originalProduct.vendors?.map((vendor) => ({
        id: String(vendor.id),
        value: vendor.name || "",
      })) || [],
    );
    productStore.updateHSNCode(originalProduct.hsn_code || "");
    productStore.updateGSTCode(originalProduct.gst_percentage || 0);
    productStore.updateFastProcurement(
      originalProduct.fast_procurement || false,
    );
    productStore.updateZohoID(originalProduct.zoho_id || "");
    productStore.updatePrintingType(
      originalProduct.printing_types?.map((printingType) => ({
        id: String(printingType.id),
        value: printingType.name || "",
      })) || [],
    );
    productStore.updatePreferredPrintingTypeId(
      originalProduct.preferred_printing_type_id ?? null,
    );
    setPreferredVendorId(originalProduct.preferred_vendor_id ?? null);
  };

  const resetSEOSection = () => {
    const originalProduct = productStore.originalProduct;
    productStore.updateSEOTitle(originalProduct.seo_title || "");
    productStore.updateSEODescription(originalProduct.seo_description || "");
  };

  const resetMediaSection = () => {
    const originalProduct = productStore.originalProduct;
    productStore.productDetails.image_urls = [
      ...(originalProduct.image_urls || []),
    ];
    productStore.productDetails.size_chart_url = originalProduct.size_chart_url;
    setSelectedImages([]);
    // setSelectedProductImage(undefined);
    productStore.clearSelectedSizeChartFile();
  };

  const saveProductImages = () => {
    setIsLoading(true);

    // Check if size chart has changed (either new file selected, existing one removed, or existing one changed)
    const hasSizeChartChanged =
      productStore.selectedSizeChartFile ||
      productStore.productDetails.size_chart_url !==
        productStore.originalProduct.size_chart_url;

    // Prepare the request payload
    const requestPayload = {
      product: {
        id: productStore.productDetails.id,
        image_urls: productStore.productDetails.image_urls || [],
        size_chart_url: productStore.productDetails.size_chart_url || "",
      },
      size_chart: hasSizeChartChanged ? 1 : 0,
    };

    // If there are new images to upload, upload them first
    if (selectedImages.length > 0) {
      const imageAPI = new ImageAPI();
      imageAPI
        .uploadImage(selectedImages, "product-images")
        .then((uploadedUrls) => {
          const newImageData: ProductImage[] = uploadedUrls.map((url) => {
            return { url: url } as ProductImage;
          });

          // Add new images to the store (this method now prevents duplicates)
          productStore.addImages(newImageData);

          // Update the request payload with the current image URLs from the store
          // The addImages method now ensures no duplicates, so we can safely use the store's image_urls
          requestPayload.product.image_urls =
            productStore.productDetails.image_urls || [];

          // Now save everything together
          saveMediaToAPI(requestPayload);
        })
        .catch((err) => {
          setIsLoading(false);
          showError(
            "Error occurred",
            err?.response?.data?.message ||
              "Something went wrong! Please try again later.",
          );
        });
    } else {
      // No new images to upload, save directly
      saveMediaToAPI(requestPayload);
    }
  };

  const saveMediaToAPI = async (requestPayload: any) => {
    try {
      // If size chart has changed and there's a new file, upload it first
      if (
        requestPayload.size_chart === 1 &&
        productStore.selectedSizeChartFile
      ) {
        const imageAPI = new ImageAPI();
        const uploadedUrls = await imageAPI.uploadImage(
          [productStore.selectedSizeChartFile],
          "product-images",
        );
        if (uploadedUrls.length > 0) {
          const newSizeChartUrl = uploadedUrls[0];
          requestPayload.product.size_chart_url = newSizeChartUrl;
          // Update the product details with the new size chart URL
          productStore.addSizeChartImage(newSizeChartUrl);
        }
      }

      // Final safeguard: ensure no duplicate images are sent to the API
      if (
        requestPayload.product.image_urls &&
        requestPayload.product.image_urls.length > 0
      ) {
        const uniqueImages = requestPayload.product.image_urls.filter(
          (img: ProductImage, index: number, self: ProductImage[]) =>
            index === self.findIndex((t: ProductImage) => t.url === img.url),
        );
        requestPayload.product.image_urls = uniqueImages;
      }

      // Now save everything to the API
      const response = await productStore.updateProductImages(requestPayload);

      if (response) {
        // Clear local states
        setSelectedImages([]);
        if (productStore.selectedSizeChartFile) {
          productStore.clearSelectedSizeChartFile();
        }

        // Update the original product to reflect the new state
        // This prevents duplication on refresh
        productStore.originalProduct = {
          ...productStore.productDetails,
          image_urls: [...(productStore.productDetails.image_urls || [])],
          size_chart_url: productStore.productDetails.size_chart_url,
        };

        setIsLoading(false);
        showSuccess("Media updated successfully");
      }
    } catch (err: any) {
      setIsLoading(false);
      showError(
        "Error occurred",
        err?.response?.data?.message ||
          err?.message ||
          "Something went wrong! Please try again later.",
      );
    }
  };

  const navigateProductToVariant = () => {
    navigate(`/products/add-edit-product/variants?product-id=${productId}`);
  };

  const handleOpenActivityDrawer = () => {
    if (!productId) return;
    setIsActivityDrawerOpen(true);
    setIsActivityLoading(true);
    productStore
      .getProductActivityLogs(Number(productId))
      .catch((err) => console.log("error in getProductActivityLogs", err))
      .finally(() => setIsActivityLoading(false));
  };

  const handleAddToPod = async () => {
    if (!productId) return;
    setAddToPodLoading(true);
    podCatalogStore
      .addToPod(Number(productId))
      .then((res) => {
        if (res.success) {
          showSuccess(res.message || "Product added successfully");
          productStore.setProductPodCatalogStatus(Number(productId), true);
        } else {
          showError("Error occurred", res.message || "Request failed.");
        }
      })
      .catch((err: any) => {
        showError(
          "Error occurred",
          err?.response?.data?.message ||
            err?.message ||
            "Something went wrong! Please try again later.",
        );
      })
      .finally(() => setAddToPodLoading(false));
  };

  useEffect(() => {
    const numericProductId = Number(productId);
    if (!Number.isFinite(numericProductId) || numericProductId <= 0) return;

    setPsdLoadingOptions(true);
    Promise.all([
      mockupConfigAPI.getPlacementTypes(),
      podCatalogStore.fetchProductPsdTemplates(numericProductId),
    ])
      .then(([placementData, templates]) => {
        podCatalogStore.setDetailPsdPlacementTypes(placementData || []);
        podCatalogStore.setProductPsdTemplates(templates || []);
      })
      .catch((err: any) => {
        showError(
          "Error occurred",
          err?.response?.data?.message ||
            err?.message ||
            "Unable to load PSD data.",
        );
      })
      .finally(() => setPsdLoadingOptions(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const handleUploadPodPsd = async () => {
    const numericProductId = Number(productId);
    if (!Number.isFinite(numericProductId) || numericProductId <= 0) return;

    const parsedPlacement = Number(podCatalogStore.detailPsdPlacementTypeId);
    if (!Number.isFinite(parsedPlacement) || parsedPlacement <= 0) {
      showError("Error occurred", "Select a placement type before uploading.");
      return;
    }
    if (!podCatalogStore.detailPsdFile) {
      showError(
        "Error occurred",
        "Choose a PSD file for the selected placement.",
      );
      return;
    }

    setPsdUploading(true);
    try {
      const res = await podCatalogStore.uploadProductPsd({
        file: podCatalogStore.detailPsdFile,
        product_id: numericProductId,
        placetype_id: parsedPlacement,
      });
      podCatalogStore.setDetailPsdFile(null);
      await podCatalogStore.loadProductPsdTemplates(numericProductId);
      showSuccess(res?.message || "PSD uploaded successfully.");
    } catch (err: any) {
      showError(
        "Error occurred",
        err?.response?.data?.message || err?.message || "Unable to upload PSD.",
      );
    } finally {
      setPsdUploading(false);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          backgroundColor: theme.palette.customColors.white,
          borderRadius: 1,
          marginLeft: 2,
          marginRight: 2,
        }}
      >
        <Box
          sx={{
            paddingLeft: 2,
            paddingTop: 2,
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            cursor: "pointer",
            color: theme.palette.customColors.greyTextColor,
            maxWidth: "3.75rem",
            "&:hover, &:active": {
              color: theme.palette.customColors.greyTextColor,
            },
          }}
          onClick={() => {
            navigate(`/products/listing`);
          }}
        >
          <ArrowBackIcon sx={{ fontSize: "1rem" }} />
          <span style={{ fontSize: "0.875rem", fontWeight: "600" }}>Back</span>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            backgroundColor: theme.palette.customColors.white,
            p: 2,
            borderRadius: 1,
          }}
        >
          <Box>
            <CustomTypography variant="heading4">
              {productId ? "Update Product" : "Create Product"}
            </CustomTypography>
            <CustomTypography
              variant="body2"
              color={theme.palette.customColors.greyTextColor}
              style={{ marginTop: 5 }}
            >
              Create and manage your product information with ease.
            </CustomTypography>
            {productStore?.productDetails?.urgent_sourcing_details?.length &&
              productStore.productDetails.urgent_sourcing_details.length >
                0 && (
                <CustomTypography
                  variant="body2"
                  color={theme.palette.customColors.greyTextColor}
                  style={{ marginTop: 5 }}
                >
                  {"Urgent Sourcing ID- " +
                    productStore.productDetails.urgent_sourcing_details[0].id}
                </CustomTypography>
              )}
          </Box>
          {productId && (
            <Tooltip title="View activity history">
              <IconButton
                onClick={handleOpenActivityDrawer}
                sx={{
                  color: theme.palette.customColors.greyTextColor,
                  border: `1px solid ${theme.palette.customColors.greyBackground}`,
                  borderRadius: "8px",
                }}
              >
                <HistoryIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {productId && (
          <Box
            sx={{
              px: 2,
              pt: 0,
              pb: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Tabs
              value={0}
              onChange={(_, tab) => {
                if (tab === 1) navigate(`/products/pod-catalog/${productId}`);
              }}
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                minHeight: "auto",
                flex: 1,
                "& .MuiTab-root": { py: 1, minHeight: "auto" },
              }}
            >
              <Tab label="Product Detail" />
              <Tab label="POD Product Detail" />
            </Tabs>
            <AppButton
              label={addToPodLoading ? "Adding..." : "Add to POD"}
              variant="primary"
              onClick={handleAddToPod}
              disabled={addToPodLoading || isInPodCatalog}
              customStyles={{
                minWidth: 130,
                opacity: isInPodCatalog ? 0.6 : 1,
              }}
            />
          </Box>
        )}
      </Box>

      <Box px={2}>
        {isLoading ? (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Card sx={{ marginTop: 2, marginBottom: 2 }}>
              <CardHeader
                onClick={() => productStore.toggleSection("product")}
                title={
                  <Box display="flex" alignItems="center" gap={1}>
                    <img
                      src={ProductIcon}
                      alt="Product"
                      style={{ width: 30, height: 30 }}
                    />
                    <CustomTypography variant="heading5">
                      Product Information
                    </CustomTypography>
                    <Chip
                      label="Core Details"
                      size="small"
                      sx={{
                        ml: 1,
                        borderRadius: 2,
                        backgroundColor: theme.palette.customColors.white,
                        color: theme.palette.customColors.greyTextColor,
                        border: `1px solid ${theme.palette.customColors.greyBackground}`,
                        height: "20px",
                        fontSize: "0.65rem",
                        "& .MuiChip-label": {
                          px: 0.8,
                          py: 0,
                        },
                      }}
                    />
                  </Box>
                }
                action={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{ display: { xs: "none", sm: "block" } }}>
                      <CustomTypography variant="body2" color="text.secondary">
                        12 fields
                      </CustomTypography>
                    </Box>
                    <IconButton>
                      {productStore.openSections.product ? (
                        <ExpandLess />
                      ) : (
                        <ExpandMore />
                      )}
                    </IconButton>
                  </Box>
                }
                sx={{
                  cursor: "pointer",
                  px: 3,
                  py: 2,
                  borderBottom: `1px solid ${theme.palette.customColors.greyBackground}`,
                  "&:hover": {
                    backgroundColor: theme.palette.customColors.greyBackground,
                  },
                }}
              />

              <Collapse
                in={productStore.openSections.product}
                timeout="auto"
                unmountOnExit
              >
                <CardContent sx={{ px: 4 }}>
                  <Box
                    mt={1}
                    display="grid"
                    gridTemplateColumns={{ xs: "1fr", lg: "7fr 1px 4fr" }}
                    gap={4}
                  >
                    <Box display="flex" flexDirection="column" gap={2}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <img
                          src={NotesIcon}
                          alt="Search"
                          style={{ width: 17, height: 17 }}
                        />
                        <CustomTypography variant="heading8">
                          Name{" "}
                          <span
                            style={{
                              color: theme.palette.red.main,
                            }}
                          >
                            *
                          </span>
                        </CustomTypography>
                      </Box>
                      <FormSmallTextField
                        label=""
                        placeholder="Enter product name"
                        variant="outlined"
                        color="secondary"
                        type="name"
                        fontSize="14px"
                        disabled={isViewOnly()}
                        value={productStore.productDetails?.name || ""}
                        onChange={(id, value) =>
                          productStore.updateName(String(value))
                        }
                        borderColor={theme.palette.customColors.greyBackground}
                      />
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        mb={1}
                        sx={{ maxWidth: "100%" }}
                      >
                        <img
                          src={NotesIcon}
                          alt="Search"
                          style={{ width: 17, height: 17 }}
                        />
                        <CustomTypography variant="heading8">
                          Product Description{" "}
                          <span
                            style={{
                              color: theme.palette.red.main,
                            }}
                          >
                            *
                          </span>
                        </CustomTypography>
                      </Box>
                      <Box
                        sx={{
                          width: "100%",
                          zIndex: 1000,
                          maxWidth: "100%",
                          overflowX: "hidden",
                          "& .ql-editor": {
                            maxWidth: "100%",
                            overflowWrap: "break-word",
                            wordWrap: "break-word",
                            width: "100%",
                            boxSizing: "border-box",
                          },
                          "& .ql-container": {
                            maxWidth: "100%",
                            width: "100%",
                            boxSizing: "border-box",
                          },
                          "& .ql-toolbar": {
                            maxWidth: "100%",
                            width: "100%",
                            boxSizing: "border-box",
                          },
                          "& .ql-editor p": {
                            maxWidth: "100%",
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                          },
                          "& .ql-editor div": {
                            maxWidth: "100%",
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                          },
                          "& .ql-editor span": {
                            maxWidth: "100%",
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                          },
                        }}
                      >
                        <Box sx={{ width: "100%", maxWidth: "100%" }}>
                          <RichTextEditor
                            value={productStore.productDetails?.desc || ""}
                            onChange={(value: string) =>
                              productStore.updateDescription(String(value))
                            }
                            maxLength={1000}
                            placeholder="Please write description here..."
                            disabled={isViewOnly()}
                            minHeight="100px"
                          />
                        </Box>
                      </Box>
                    </Box>

                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{
                        display: { xs: "none", md: "block" },
                        border: "none",
                        background: `linear-gradient(to bottom, ${theme.palette.customColors.dividerGradientTransparent}, ${theme.palette.customColors.dividerGradientMid}, ${theme.palette.customColors.dividerGradientTransparent})`,
                      }}
                    />

                    <Box display="flex" flexDirection="column" gap={2}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <img
                          src={StatusIcon}
                          alt="Search"
                          style={{ width: 17, height: 17 }}
                        />
                        <CustomTypography variant="heading8">
                          Status{" "}
                          <span
                            style={{
                              color: theme.palette.red.main,
                            }}
                          >
                            *
                          </span>
                        </CustomTypography>
                      </Box>
                      <AppDropDown
                        isMultiSelect={false}
                        title=""
                        value={
                          productStore.productDetails?.active !== undefined
                            ? Boolean(productStore.productDetails?.active)
                              ? "true"
                              : "false"
                            : "-1"
                        }
                        list={activeFilters}
                        height="36px"
                        listHeight="30px"
                        disabled={isViewOnly()}
                        fontSize="14px"
                        onSingleSelectionChange={(id) => {
                          productStore.updateProductStatus(id === "true");
                        }}
                        borderColor={theme.palette.customColors.greyBackground}
                      />

                      <Box
                        p={2.2}
                        borderRadius={2}
                        border="1px solid"
                        borderColor={theme.palette.customColors.greyBackground}
                        sx={{
                          bgcolor:
                            theme.palette.customColors.lightGreenBackground,
                        }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Box display="flex" gap={1} alignItems="center">
                            <img
                              src={EyeIcon}
                              alt="Flash"
                              style={{ width: 17, height: 17 }}
                            />
                            <Box>
                              <CustomTypography
                                variant="heading8"
                                style={{ paddingBottom: "5px" }}
                              >
                                Visibility
                              </CustomTypography>
                              <CustomTypography variant="body3">
                                Show on website
                              </CustomTypography>
                            </Box>
                          </Box>
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            onClick={() =>
                              productStore.updateDisplay(
                                !productStore.productDetails?.display,
                              )
                            }
                            sx={{
                              cursor: isViewOnly() ? "not-allowed" : "pointer",
                            }}
                          >
                            <Switch
                              size="small"
                              checked={!!productStore.productDetails?.display}
                              disabled={isViewOnly()}
                              onChange={() => {}}
                              color="success"
                              sx={{
                                pointerEvents: "none",
                              }}
                            />
                            <CustomTypography
                              variant="body2"
                              color={
                                productStore.productDetails?.display
                                  ? "success.main"
                                  : "text.secondary"
                              }
                              fontWeight={500}
                            >
                              {productStore.productDetails?.display
                                ? "ON"
                                : "OFF"}
                            </CustomTypography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  <Box
                    display="grid"
                    gridTemplateColumns={{ xs: "1fr", lg: "7fr 1px 4fr" }}
                    gap={4}
                  >
                    <Box display="flex" flexDirection="column" gap={3}>
                      <GradientDivider
                        title="Classification"
                        color="173, 216, 255"
                        sx={{ my: 4 }}
                      />
                      <Box width="100%">
                        <Box>
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            mb={2}
                          >
                            <img
                              src={StatusIcon}
                              alt="Business"
                              style={{ width: 17, height: 17 }}
                            />
                            <CustomTypography variant="heading8">
                              Categories
                            </CustomTypography>
                            <CustomTypography variant="heading8">
                              <span
                                style={{
                                  color: theme.palette.red.main,
                                }}
                              >
                                *
                              </span>
                            </CustomTypography>
                          </Box>
                          <Box>
                            {categoryFilters.length !== 0 && (
                              <AppSearchDropDown
                                required
                                isMultiSelect={true}
                                title="Select Category"
                                disabled={isViewOnly()}
                                value={
                                  productStore.productDetails.categories
                                    ? productStore.productDetails.categories.map(
                                        (
                                          category: ProductCategoriesMapping,
                                        ) => ({
                                          id: String(category.category_id),
                                          value: category.category_name || "",
                                        }),
                                      )
                                    : productStore.productDetails.category_id
                                      ? [
                                          {
                                            id: String(
                                              productStore.productDetails
                                                .category_id,
                                            ),
                                            value:
                                              productStore.productDetails
                                                .category_name || "",
                                          },
                                        ]
                                      : []
                                }
                                fontSize="14px"
                                list={categoryFilters}
                                local={true}
                                handleSelect={(value) =>
                                  productStore.updateCategory(value)
                                }
                                borderColor={
                                  theme.palette.customColors.greyBackground
                                }
                              />
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Box>

                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{
                        display: { xs: "none", md: "block" },
                        border: "none",
                        background: `linear-gradient(to bottom, ${theme.palette.customColors.dividerGradientTransparent}, ${theme.palette.customColors.dividerGradientMid}, ${theme.palette.customColors.dividerGradientTransparent})`,
                      }}
                    />

                    <Box display="flex" flexDirection="column">
                      <GradientDivider
                        title="Pricing"
                        color="0, 255, 170"
                        sx={{ my: 1, marginTop: "-8rem" }}
                      />
                      <Box>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          <img
                            src={RupeeIcon}
                            alt="RupeeIcon"
                            style={{ width: 17, height: 17 }}
                          />
                          <CustomTypography variant="heading8">
                            Cost Price{" "}
                            <span
                              style={{
                                color: theme.palette.red.main,
                              }}
                            >
                              *
                            </span>
                          </CustomTypography>
                        </Box>

                        <FormTextField
                          label=""
                          placeholder="0.00"
                          required
                          variant="outlined"
                          color="secondary"
                          type="number"
                          fontSize="14px"
                          disabled={isViewOnly() || !canChangeCostPrice()}
                          value={
                            canChangeCostPrice()
                              ? productStore.productDetails?.cost_price &&
                                !isNaN(
                                  Number(
                                    productStore.productDetails.cost_price,
                                  ),
                                )
                                ? String(productStore.productDetails.cost_price)
                                : ""
                              : "0"
                          }
                          onChange={(id, value) =>
                            productStore.updateCostPrice(Number(value))
                          }
                          borderColor={
                            theme.palette.customColors.greyBackground
                          }
                        />
                      </Box>
                      <Box>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          <img
                            src={RupeeIcon}
                            alt="RupeeIcon"
                            style={{ width: 17, height: 17 }}
                          />
                          <CustomTypography variant="heading8">
                            Selling Price{" "}
                            <span
                              style={{
                                color: theme.palette.red.main,
                              }}
                            >
                              *
                            </span>
                          </CustomTypography>
                        </Box>
                        <FormTextField
                          label=""
                          placeholder="0.00"
                          required
                          variant="outlined"
                          color="secondary"
                          type="number"
                          fontSize="14px"
                          disabled={isViewOnly() || !canChangeSellingPrice()}
                          value={
                            productStore.productDetails?.selling_price &&
                            !isNaN(
                              Number(productStore.productDetails.selling_price),
                            )
                              ? String(
                                  productStore.productDetails.selling_price,
                                )
                              : ""
                          }
                          onChange={(id, value) =>
                            productStore.updateSellingPrice(Number(value))
                          }
                          borderColor={
                            theme.palette.customColors.greyBackground
                          }
                        />
                      </Box>
                      <Box>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          <img
                            src={SkuIcon}
                            alt="Business"
                            style={{ width: 15, height: 15 }}
                          />
                          <CustomTypography variant="heading8">
                            SKU{" "}
                          </CustomTypography>
                        </Box>
                        <FormTextField
                          label=""
                          placeholder="Enter SKU code"
                          variant="outlined"
                          color="secondary"
                          type="sku"
                          fontSize="14px"
                          disabled={isViewOnly()}
                          value={productStore.productDetails?.sku || ""}
                          onChange={(id, value) =>
                            productStore.updateSKU(value)
                          }
                          borderColor={
                            theme.palette.customColors.greyBackground
                          }
                        />
                      </Box>
                    </Box>
                  </Box>

                  <GradientDivider
                    title="Product Variants"
                    color="255, 178, 102"
                    sx={{ my: 3 }}
                  />
                  <Box
                    display="grid"
                    gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
                    gap={4}
                  >
                    <Box>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <img
                          src={StatusIcon}
                          alt="Variant"
                          style={{ width: 17, height: 17 }}
                        />
                        <CustomTypography variant="heading8">
                          Variant Options
                        </CustomTypography>
                        <Tooltip
                          title="OneSize variant is added by default. Products cannot be added/updated without a size variant. 
              In case there is a need to remove a 'OneSize' variant, please add a custom size variant"
                        >
                          <Info
                            fontSize="small"
                            color="action"
                            sx={{ cursor: "pointer" }}
                          />
                        </Tooltip>
                      </Box>

                      {productStore.productOptions &&
                        productStore.productOptions.length !== 0 && (
                          <AddProductVariants
                            productId={productStore.productDetails.id!}
                            options={productStore.productOptions}
                            variants={
                              productStore.productDetails.variants || []
                            }
                            isViewOnly={isViewOnly()}
                            onSave={(variants) => {
                              productStore.updateProductOptions(variants);
                            }}
                            onClear={() => {
                              productStore.resetProductOptions();
                            }}
                            canChangeCostPrice={canChangeCostPrice()}
                          />
                        )}
                    </Box>

                    <Box>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <img
                          src={ImageIcon}
                          alt="Variant"
                          style={{ width: 17, height: 17 }}
                        />
                        <CustomTypography variant="heading8">
                          Variant Images
                        </CustomTypography>
                      </Box>
                      <AppButton
                        startIcon={<img src={UploadIcon} alt="Search" />}
                        label="Upload Variant Images"
                        variant="grayscale"
                        onClick={navigateProductToVariant}
                        disabled={isViewOnly() || productId === null}
                        customStyles={{
                          width: "100%",
                          marginTop: 2,
                          color: theme.palette.common.black,
                          border: "2px dashed",
                          borderColor:
                            theme.palette.customColors.greyBackground,
                          backgroundColor: "transparent",
                          "&:hover": {
                            border: `2px dashed ${theme.palette.orange.light}`,
                            backgroundColor:
                              theme.palette.customColors.greyBackground,
                            color: theme.palette.common.black,
                          },
                        }}
                      />
                    </Box>
                  </Box>

                  <Divider sx={{ my: 4 }} />
                  <Box
                    display="flex"
                    justifyContent="flex-end"
                    alignItems={{ xs: "stretch", sm: "center" }}
                    flexDirection={{ xs: "column", sm: "row" }}
                    gap={{ xs: 2, sm: 0 }}
                  >
                    <Box
                      display="flex"
                      gap={2}
                      flexDirection={{ xs: "column", sm: "row" }}
                    >
                      <AppButton
                        label="Reset"
                        variant="grayscale"
                        onClick={() => resetProductSection()}
                        customStyles={{
                          width: { xs: "100%", sm: 120, md: 150 },
                        }}
                      />
                      <AppButton
                        label="Save Product"
                        onClick={() => saveDetails()}
                        customStyles={{
                          width: { xs: "100%", sm: 120, md: 150 },
                          background: `linear-gradient(90deg, ${theme.palette.customColors.tealGradientStart} 0%, ${theme.palette.customColors.tealGradientEnd} 100%)`,
                          color: theme.palette.customColors.white,
                          "&:hover": {
                            background: `linear-gradient(90deg, ${theme.palette.customColors.tealGradientEnd} 0%, ${theme.palette.customColors.tealGradientStart} 100%)`,
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Collapse>
            </Card>

            {productId && (
              <Card sx={{ marginBottom: 4 }}>
                <CardHeader
                  title={
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <img
                          src={SearchIcon}
                          alt="Search"
                          style={{ width: 30, height: 30, color: "white" }}
                        />
                      </Box>
                      <CustomTypography variant="heading5">
                        Media & Assets
                      </CustomTypography>
                      <Chip
                        label="Media & Assets"
                        size="small"
                        sx={{
                          ml: 1,
                          borderRadius: 2,
                          backgroundColor: theme.palette.customColors.white,
                          color: theme.palette.customColors.greyTextColor,
                          border: `1px solid ${theme.palette.customColors.greyBackground}`,
                          height: "20px",
                          fontSize: "0.65rem",
                          "& .MuiChip-label": {
                            px: 0.8,
                            py: 0,
                          },
                        }}
                      />
                    </Box>
                  }
                  action={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box sx={{ display: { xs: "none", sm: "block" } }}>
                        <CustomTypography
                          variant="body2"
                          color="text.secondary"
                        >
                          2 fields
                        </CustomTypography>
                      </Box>
                      <IconButton>
                        {productStore.openSections.media ? (
                          <ExpandLess />
                        ) : (
                          <ExpandMore />
                        )}
                      </IconButton>
                    </Box>
                  }
                  sx={{
                    cursor: "pointer",
                    px: 3,
                    py: 2,
                    borderBottom: `1px solid ${theme.palette.customColors.greyBackground}`,
                    "&:hover": {
                      backgroundColor:
                        theme.palette.customColors.greyBackground,
                    },
                  }}
                  onClick={() => productStore.toggleSection("media")}
                />

                <Collapse
                  in={productStore.openSections.media}
                  timeout="auto"
                  unmountOnExit
                >
                  <CardContent sx={{ px: 4 }}>
                    <Box>
                      <Box
                        flexDirection={{ xs: "column", sm: "row" }}
                        gap={4}
                        mb={4}
                        sx={{
                          width: "100%",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            width: { xs: "100%" },
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            <img
                              src={NotesIcon}
                              alt="Search"
                              style={{ width: 17, height: 17 }}
                            />
                            <CustomTypography variant="heading8">
                              Product Image{" "}
                              <span
                                style={{
                                  color: theme.palette.red.main,
                                }}
                              >
                                *
                              </span>
                            </CustomTypography>
                          </Box>
                          <Box sx={{ width: "100%", maxWidth: "100%" }} mt={2}>
                            <MediaAssetsUpload
                              uploadIcon={
                                <img
                                  src={FileUploadIcon}
                                  alt="Upload"
                                  width={40}
                                  height={40}
                                />
                              }
                              fileInfo="PNG, JPG up to 2MB each"
                              isViewOnly={isViewOnly()}
                              existingImageList={
                                productStore.productDetails.image_urls?.map(
                                  (image) => {
                                    return {
                                      id: image.id,
                                      title: "Product Image",
                                      img: image.url,
                                      is_default: image.is_default,
                                    } as ImageData;
                                  },
                                ) || []
                              }
                              newImageList={selectedImages}
                              deleteImage={(image: ImageData) => {
                                productStore.removeImage({
                                  id: image.id,
                                  url: image.img,
                                });
                              }}
                              removeImage={(image: {
                                id: string | number;
                                title?: string;
                                img: string;
                              }) => {
                                // Use a more stable filtering approach to prevent unnecessary re-renders
                                const updatedImages = selectedImages.filter(
                                  (file) => file.name !== image.title,
                                );
                                if (
                                  updatedImages.length !== selectedImages.length
                                ) {
                                  setSelectedImages(updatedImages);
                                }
                              }}
                              addImagesToBeUploaded={(files: File[]) =>
                                setSelectedImages([...selectedImages, ...files])
                              }
                              onSetDefaultImage={async (imageId) => {
                                try {
                                  await productStore.setDefaultImage(imageId);
                                } catch (error) {
                                  console.error(
                                    "Error setting default image:",
                                    error,
                                  );
                                }
                              }}
                            />
                          </Box>
                        </Box>

                        <Box
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            width: { xs: "100%" },
                          }}
                          mt={4}
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            mb={2.5}
                          >
                            <img
                              src={NotesIcon}
                              alt="Search"
                              style={{ width: 17, height: 17 }}
                            />
                            <CustomTypography variant="heading8">
                              Size Chart{" "}
                              <span
                                style={{
                                  color: theme.palette.red.main,
                                }}
                              >
                                *
                              </span>
                            </CustomTypography>
                          </Box>
                          <Box sx={{ width: "100%", maxWidth: "100%" }}>
                            <MediaAssetsUpload
                              key={`size-chart-${
                                productStore.productDetails.size_chart_url
                                  ? "has-image"
                                  : "no-image"
                              }-${
                                productStore.selectedSizeChartFile
                                  ? "has-file"
                                  : "no-file"
                              }`}
                              maxWidth={"100%"}
                              uploadText="Upload size chart"
                              fileInfo="PNG, JPG up to 2MB each"
                              multiple={false}
                              existingImageList={
                                productStore.selectedSizeChartFile
                                  ? [] // If new file is selected, don't show existing image
                                  : productStore.productDetails.size_chart_url
                                    ? [
                                        {
                                          img: productStore.productDetails
                                            .size_chart_url,
                                          title: "Size Chart",
                                        },
                                      ]
                                    : []
                              }
                              newImageList={
                                productStore.selectedSizeChartFile
                                  ? [productStore.selectedSizeChartFile]
                                  : []
                              }
                              deleteImage={() => {
                                // Remove existing size chart image
                                productStore.removeSizeChartImage();
                                // Clear any selected file as well
                                productStore.clearSelectedSizeChartFile();
                              }}
                              removeImage={(image: {
                                id: string | number;
                                title?: string;
                                img: string;
                              }) => {
                                // Remove existing size chart image
                                productStore.removeSizeChartImage();
                                // Clear any selected file as well
                                productStore.clearSelectedSizeChartFile();
                              }}
                              addImagesToBeUploaded={(files: File[]) => {
                                if (files.length > 0) {
                                  // Clear existing size chart URL when new file is selected
                                  productStore.removeSizeChartImage();
                                  productStore.setSelectedSizeChartFile(
                                    files[0],
                                  );
                                }
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>

                  <CardActions
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: { xs: "stretch", sm: "center" },
                      flexDirection: { xs: "column", sm: "row" },
                      gap: { xs: 2, sm: 0 },
                      px: 4,
                      py: 3,
                      borderTop: `1px solid ${theme.palette.customColors.lightBorder}`,
                    }}
                  >
                    <Box
                      display="flex"
                      gap={2}
                      flexDirection={{ xs: "column", sm: "row" }}
                    >
                      <AppButton
                        label="Reset"
                        variant="grayscale"
                        onClick={() => resetMediaSection()}
                        customStyles={{
                          width: { xs: "100%", sm: 120, md: 150 },
                        }}
                      />
                      <AppButton
                        label="Save Media"
                        disabled={
                          !userStore.hasPermission(EmployeeRoles.EDIT_PRODUCT)
                        }
                        onClick={() => saveProductImages()}
                        customStyles={{
                          width: { xs: "100%", sm: 120, md: 150 },
                          background: `linear-gradient(90deg, ${theme.palette.customColors.purpleGradientStart} 0%, ${theme.palette.customColors.purpleGradientEnd} 100%)`,
                          color: theme.palette.customColors.white,
                          "&:hover": {
                            background: `linear-gradient(90deg, ${theme.palette.customColors.purpleGradientEnd} 0%, ${theme.palette.customColors.purpleGradientStart} 100%)`,
                          },
                        }}
                      />
                    </Box>
                  </CardActions>
                </Collapse>
              </Card>
            )}

            {productId && Number(productId) > 0 && (
              <PodProductPsdMappingsCard
                loadingOptions={psdLoadingOptions}
                uploading={psdUploading}
                onUploadPsd={handleUploadPodPsd}
              />
            )}

            {productId && (
              <Card sx={{ marginTop: 3, marginBottom: 3 }}>
                <CardHeader
                  onClick={() => productStore.toggleSection("sourcing")}
                  title={
                    <Box display="flex" alignItems="center" gap={1}>
                      <img
                        src={SourcingIcon}
                        alt="Sourcing"
                        style={{ width: 30, height: 30 }}
                      />
                      <CustomTypography variant="heading5">
                        Sourcing
                      </CustomTypography>
                      <Chip
                        label="Supply Chain"
                        size="small"
                        sx={{
                          ml: 1,
                          borderRadius: 2,
                          backgroundColor: theme.palette.customColors.white,
                          color: theme.palette.customColors.greyTextColor,
                          border: `1px solid ${theme.palette.customColors.greyBackground}`,
                          height: "20px",
                          fontSize: "0.65rem",
                          "& .MuiChip-label": {
                            px: 0.8,
                            py: 0,
                          },
                        }}
                      />
                    </Box>
                  }
                  action={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box sx={{ display: { xs: "none", sm: "block" } }}>
                        <CustomTypography
                          variant="body2"
                          color="text.secondary"
                        >
                          6 fields
                        </CustomTypography>
                      </Box>
                      <IconButton>
                        {productStore.openSections.sourcing ? (
                          <ExpandLess />
                        ) : (
                          <ExpandMore />
                        )}
                      </IconButton>
                    </Box>
                  }
                  sx={{
                    cursor: "pointer",
                    px: 3,
                    py: 2,
                    borderBottom: `1px solid ${theme.palette.customColors.lightBorder}`,
                    "&:hover": {
                      backgroundColor: theme.palette.customColors.subtleOverlay,
                    },
                  }}
                />

                <Collapse
                  in={productStore.openSections.sourcing}
                  timeout="auto"
                  unmountOnExit
                >
                  <CardContent sx={{ px: 4 }}>
                    <Box
                      display="grid"
                      gridTemplateColumns={{ xs: "1fr", lg: "7fr 1px 4fr" }}
                      gap={4}
                    >
                      <Box display="flex" flexDirection="column" gap={4}>
                        <Box
                          display="grid"
                          gridTemplateColumns={{ md: "1fr 1fr" }}
                          gap={3}
                        >
                          <Box>
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={1}
                              mb={2}
                            >
                              <img
                                src={ColorPadIcon}
                                alt="Preferred Printing Type"
                                style={{ width: 17, height: 17 }}
                              />
                              <CustomTypography variant="heading8">
                                Preferred Printing Type
                              </CustomTypography>
                            </Box>
                            <AppDropDown
                              title=""
                              isMultiSelect={false}
                              disabled={
                                !userStore.hasPermission(
                                  EmployeeRoles.EDIT_PRODUCT_SOURCING_DETAILS,
                                )
                              }
                              list={[
                                { id: "-1", value: "Select" },
                                ...(productStore.printingTypes || []).map(
                                  (printingType) => ({
                                    id: String(printingType.id),
                                    value: printingType.name || "",
                                  }),
                                ),
                              ]}
                              value={
                                productStore.productDetails
                                  .preferred_printing_type_id != null
                                  ? String(
                                      productStore.productDetails
                                        .preferred_printing_type_id,
                                    )
                                  : "-1"
                              }
                              height="36px"
                              listHeight="30px"
                              fontSize="14px"
                              onSingleSelectionChange={(id) =>
                                productStore.updatePreferredPrintingTypeId(
                                  id === "-1" ? null : Number(id),
                                )
                              }
                              borderColor={
                                theme.palette.customColors.greyBackground
                              }
                            />
                          </Box>
                          <Box>
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={1}
                              mb={2}
                            >
                              <img
                                src={ColorPadIcon}
                                alt="Business"
                                style={{ width: 17, height: 17 }}
                              />

                              <CustomTypography variant="heading8">
                                Printing Type
                              </CustomTypography>
                            </Box>
                            <Box>
                              {productStore.printingTypes &&
                                productStore.printingTypes.length !== 0 && (
                                  <AppSearchDropDown
                                    isMultiSelect={true}
                                    title="Select Printing Type"
                                    disabled={
                                      !userStore.hasPermission(
                                        EmployeeRoles.EDIT_PRODUCT_SOURCING_DETAILS,
                                      )
                                    }
                                    value={
                                      productStore.productDetails
                                        .printing_types &&
                                      productStore.productDetails.printing_types
                                        .length > 0
                                        ? productStore.productDetails.printing_types.map(
                                            (printingType) => ({
                                              id: String(printingType.id),
                                              value: printingType.name || "",
                                            }),
                                          )
                                        : []
                                    }
                                    fontSize="14px"
                                    list={
                                      productStore.printingTypes &&
                                      productStore.printingTypes.length > 0
                                        ? productStore.printingTypes.map(
                                            (printingType) => ({
                                              id: String(printingType.id),
                                              value: printingType.name || "",
                                            }),
                                          )
                                        : []
                                    }
                                    local={true}
                                    handleSelect={(value) =>
                                      productStore.updatePrintingType(value)
                                    }
                                    borderColor={
                                      theme.palette.customColors.greyBackground
                                    }
                                  />
                                )}
                            </Box>
                          </Box>
                          <Box>
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={1}
                              mb={2}
                            >
                              <img
                                src={BusinessIcon}
                                alt="Business"
                                style={{ width: 18, height: 18 }}
                              />
                              <CustomTypography variant="heading8">
                                Vendor Selection{" "}
                              </CustomTypography>
                            </Box>
                            <AppSearchDropDown
                              isMultiSelect={true}
                              title="Select vendors"
                              disabled={
                                !userStore.hasPermission(
                                  EmployeeRoles.EDIT_PRODUCT_SOURCING_DETAILS,
                                )
                              }
                              value={
                                productStore.productDetails.vendors
                                  ? productStore.productDetails.vendors.map(
                                      (vendor) => ({
                                        id: String(vendor.id),
                                        value: vendor.name || "",
                                      }),
                                    )
                                  : []
                              }
                              fontSize="14px"
                              list={vendorStore.vendors.map((vendor) => {
                                return {
                                  id: String(vendor.id!),
                                  value: vendor.name,
                                } as FilterValue;
                              })}
                              local={false}
                              handleSearch={(name) => searchVendor(name!)}
                              textFieldValue={
                                inputVendorNameTextRef.current || ""
                              }
                              handleSelect={(user) => {
                                productStore.updatedVendors(user);
                                if (inputVendorNameTextRef.current) {
                                  inputVendorNameTextRef.current = "";
                                }
                              }}
                              borderColor={
                                theme.palette.customColors.greyBackground
                              }
                            />
                          </Box>
                          <Box>
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={1}
                              mb={2}
                            >
                              <img
                                src={BusinessIcon}
                                alt="Preferred Vendor"
                                style={{ width: 18, height: 18 }}
                              />
                              <CustomTypography variant="heading8">
                                Preferred Vendor
                              </CustomTypography>
                            </Box>
                            <AppDropDown
                              title=""
                              isMultiSelect={false}
                              disabled={
                                !userStore.hasPermission(
                                  EmployeeRoles.EDIT_PRODUCT_SOURCING_DETAILS,
                                )
                              }
                              list={[
                                { id: "-1", value: "Select" },
                                ...(productStore.productDetails.vendors || [])
                                  .filter((vendor) => vendor.id != null)
                                  .map((vendor) => ({
                                    id: String(vendor.id),
                                    value: vendor.name || "",
                                  })),
                              ]}
                              value={
                                productStore.productDetails
                                  .preferred_vendor_id != null
                                  ? String(
                                      productStore.productDetails
                                        .preferred_vendor_id,
                                    )
                                  : "-1"
                              }
                              height="36px"
                              listHeight="30px"
                              fontSize="14px"
                              onSingleSelectionChange={(id) =>
                                setPreferredVendorId(
                                  id === "-1" ? null : Number(id),
                                )
                              }
                              borderColor={
                                theme.palette.customColors.greyBackground
                              }
                            />
                          </Box>
                        </Box>

                        <Box
                          display="grid"
                          gridTemplateColumns={{ md: "1fr 1fr" }}
                          gap={3}
                        >
                          <Box>
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={1}
                              mb={2}
                            >
                              <img
                                src={TagIcon}
                                alt="Tag"
                                style={{ width: 18, height: 18 }}
                              />
                              <CustomTypography variant="heading8">
                                HSN Code{" "}
                                <span
                                  style={{
                                    color: theme.palette.red.main,
                                  }}
                                >
                                  *
                                </span>
                              </CustomTypography>
                            </Box>
                            <FormSmallTextField
                              label="HSN Code"
                              required
                              variant="outlined"
                              color="secondary"
                              type="name"
                              fontSize="14px"
                              disabled={
                                !userStore.hasPermission(
                                  EmployeeRoles.EDIT_PRODUCT_SOURCING_DETAILS,
                                )
                              }
                              value={
                                productStore.productDetails?.hsn_code || ""
                              }
                              onChange={(id, value) =>
                                productStore.updateHSNCode(String(value).trim())
                              }
                              borderColor={
                                theme.palette.customColors.greyBackground
                              }
                            />
                          </Box>

                          <Box>
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={1}
                              mb={2}
                            >
                              <img
                                src={PercentIcon}
                                alt="Percent"
                                style={{ width: 18, height: 18 }}
                              />
                              <CustomTypography variant="heading8">
                                GST Rate{" "}
                                <span
                                  style={{
                                    color: theme.palette.red.main,
                                  }}
                                >
                                  *
                                </span>
                              </CustomTypography>
                            </Box>
                            <AppDropDown
                              title=""
                              isMultiSelect={false}
                              list={gstFilters}
                              disabled={
                                !userStore.hasPermission(
                                  EmployeeRoles.EDIT_PRODUCT_SOURCING_DETAILS,
                                )
                              }
                              required={true}
                              value={
                                productStore.productDetails.gst_percentage ===
                                  null ||
                                productStore.productDetails.gst_percentage ===
                                  undefined
                                  ? "-1"
                                  : String(
                                      productStore.productDetails
                                        .gst_percentage,
                                    )
                              }
                              height="36px"
                              listHeight="30px"
                              fontSize="14px"
                              onSingleSelectionChange={(id) => {
                                productStore.updateGSTCode(Number(id));
                              }}
                              borderColor={
                                theme.palette.customColors.greyBackground
                              }
                            />
                          </Box>
                        </Box>
                      </Box>

                      <Divider
                        orientation="vertical"
                        flexItem
                        sx={{
                          display: { xs: "none", md: "block" },
                          border: "none",
                          background: `linear-gradient(to bottom, ${theme.palette.customColors.dividerGradientTransparent}, ${theme.palette.customColors.dividerGradientMid}, ${theme.palette.customColors.dividerGradientTransparent})`,
                        }}
                      />

                      <Box display="flex" flexDirection="column" gap={4}>
                        <GradientDivider title="Settings" sx={{ my: 3 }} />

                        <Box
                          p={2.2}
                          borderRadius={2}
                          border="1px solid"
                          borderColor="divider"
                          sx={{
                            bgcolor: theme.palette.customColors.lightBlue,
                          }}
                        >
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Box display="flex" gap={1} alignItems="center">
                              <img
                                src={FlashOnIcon}
                                alt="Flash"
                                style={{ width: 16, height: 18 }}
                              />
                              <Box>
                                <CustomTypography
                                  variant="heading8"
                                  style={{ paddingBottom: "5px" }}
                                >
                                  Fast Procurement
                                </CustomTypography>
                                <CustomTypography variant="body3">
                                  Enable priority processing
                                </CustomTypography>
                              </Box>
                            </Box>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Switch
                                size="small"
                                checked={
                                  !!productStore.productDetails
                                    ?.fast_procurement
                                }
                                disabled={
                                  !userStore.hasPermission(
                                    EmployeeRoles.EDIT_PRODUCT_SOURCING_DETAILS,
                                  )
                                }
                                onClick={() => {
                                  productStore.updateFastProcurement(
                                    !productStore.productDetails
                                      ?.fast_procurement,
                                  );
                                }}
                                color="primary"
                              />
                              <CustomTypography
                                variant="body2"
                                color={
                                  productStore.productDetails?.fast_procurement
                                    ? "success.main"
                                    : "text.secondary"
                                }
                                fontWeight={500}
                              >
                                {productStore.productDetails?.fast_procurement
                                  ? "ON"
                                  : "OFF"}
                              </CustomTypography>
                            </Box>
                          </Box>
                        </Box>

                        <Box>
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            mb={2}
                          >
                            <img
                              src={BusinessIcon}
                              alt="Business"
                              style={{ width: 18, height: 18 }}
                            />
                            <CustomTypography variant="heading8">
                              Zoho Integration ID
                            </CustomTypography>
                          </Box>
                          <FormTextField
                            label="Zoho Id"
                            disabled={!canChangeZohoId()}
                            type="text"
                            value={productStore.productDetails?.zoho_id ?? ""}
                            onChange={(id, value) => {
                              productStore.updateZohoID(String(value?.trim()));
                            }}
                            borderColor={
                              theme.palette.customColors.greyBackground
                            }
                          />
                        </Box>
                      </Box>
                    </Box>

                    {userStore.hasPermission(EmployeeRoles.VIEW_COST_PRICE) && (
                      <Box
                        sx={{
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.customColors.greyBackground}`,
                          backgroundColor: theme.palette.customColors.white,
                          overflow: "hidden",
                          mb: 4,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: { xs: "flex-start", sm: "center" },
                            gap: 2,
                            px: { xs: 2, md: 3 },
                            py: 2,
                            backgroundColor:
                              theme.palette.customColors.subtleOverlay,
                            borderBottom: `1px solid ${theme.palette.customColors.greyBackground}`,
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            setPurchaseInsightsOpen((prev) => !prev)
                          }
                        >
                          <Box display="flex" alignItems="flex-start" gap={1}>
                            <img
                              src={StatusIcon}
                              alt="Previous Procurement"
                              style={{ width: 24, height: 24 }}
                            />
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.2,
                              }}
                            >
                              <CustomTypography variant="heading7">
                                Previous Procurement Insights
                              </CustomTypography>
                              <CustomTypography
                                variant="body3"
                                color="text.secondary"
                              >
                                Review historical purchase requests & negotiated
                                prices
                              </CustomTypography>
                            </Box>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              setPurchaseInsightsOpen((prev) => !prev);
                            }}
                          >
                            {purchaseInsightsOpen ? (
                              <ExpandLess fontSize="small" />
                            ) : (
                              <ExpandMore fontSize="small" />
                            )}
                          </IconButton>
                        </Box>

                        <Collapse in={purchaseInsightsOpen} timeout="auto">
                          <Box sx={{ px: { xs: 1.5, md: 3 }, py: 3 }}>
                            {productStore.purchaseRequestInsights.length ===
                              0 && (
                              <CustomTypography
                                variant="body2"
                                color="text.secondary"
                              >
                                No purchase request insights found.
                              </CustomTypography>
                            )}
                            {productStore.purchaseRequestInsights.length > 0 &&
                              productStore.purchaseRequestInsights.map(
                                (insight, index) => {
                                  const rowBg =
                                    index % 2 === 0
                                      ? theme.palette.customColors.lightBlue
                                      : theme.palette.customColors
                                          .greyBackground;
                                  const totalValue =
                                    insight.quantity * insight.rate;

                                  return (
                                    <Box
                                      key={`${insight.pr_id}-${insight.po_id}`}
                                      sx={{
                                        display: "grid",
                                        gridTemplateColumns: {
                                          xs: "1fr",
                                          sm: "repeat(2, 1fr)",
                                          lg: "1.2fr 0.9fr 0.9fr 0.8fr",
                                        },
                                        gap: { xs: 1.75, md: 2 },
                                        backgroundColor: rowBg,
                                        borderRadius: 2,
                                        px: { xs: 2, md: 3 },
                                        py: { xs: 2, md: 1.5 },
                                        mb:
                                          index ===
                                          productStore.purchaseRequestInsights
                                            .length -
                                            1
                                            ? 0
                                            : 2,
                                        border: `1px solid ${theme.palette.customColors.lightBorder}`,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: 0.2,
                                        }}
                                      >
                                        <CustomTypography
                                          variant="body3"
                                          color="text.secondary"
                                          sx={{ textTransform: "uppercase" }}
                                        >
                                          Purchase Request
                                        </CustomTypography>
                                        <CustomTypography
                                          variant="body2"
                                          fontWeight={600}
                                        >
                                          {insight.title}
                                        </CustomTypography>
                                        <CustomTypography
                                          variant="body3"
                                          color="text.secondary"
                                        >
                                          PR #{insight.pr_id} · PO #
                                          {insight.po_id}
                                        </CustomTypography>
                                      </Box>

                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: 0.2,
                                        }}
                                      >
                                        <CustomTypography
                                          variant="body3"
                                          color="text.secondary"
                                          sx={{ textTransform: "uppercase" }}
                                        >
                                          Customer
                                        </CustomTypography>
                                        <CustomTypography
                                          variant="body2"
                                          fontWeight={600}
                                        >
                                          {insight.customer_name}
                                        </CustomTypography>
                                        <CustomTypography
                                          variant="body3"
                                          color="text.secondary"
                                        >
                                          Order #{insight.order_id}
                                        </CustomTypography>
                                      </Box>

                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: 0.2,
                                        }}
                                      >
                                        <CustomTypography
                                          variant="body3"
                                          color="text.secondary"
                                          sx={{ textTransform: "uppercase" }}
                                        >
                                          Procured Qty & Rate
                                        </CustomTypography>
                                        <CustomTypography
                                          variant="body2"
                                          fontWeight={600}
                                          sx={{ display: "flex", gap: 0.5 }}
                                        >
                                          {insight.quantity} units ·{" "}
                                          {formatAmount(insight.rate)}
                                        </CustomTypography>
                                        <Chip
                                          label={`${formatAmount(
                                            totalValue,
                                          )} total`}
                                          size="small"
                                          sx={{
                                            mt: 0.5,
                                            alignSelf: "flex-start",
                                            backgroundColor:
                                              theme.palette.customColors.white,
                                            color:
                                              theme.palette.customColors
                                                .blackBorder,
                                            borderRadius: "12px",
                                            border: `1px solid ${theme.palette.customColors.greyBorder}`,
                                            fontWeight: 600,
                                          }}
                                        />
                                      </Box>

                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: 0.2,
                                        }}
                                      >
                                        <CustomTypography
                                          variant="body3"
                                          color="text.secondary"
                                          sx={{ textTransform: "uppercase" }}
                                        >
                                          Processed By
                                        </CustomTypography>
                                        <CustomTypography
                                          variant="body2"
                                          fontWeight={600}
                                        >
                                          {insight.created_by_admin_name}
                                        </CustomTypography>
                                        <CustomTypography
                                          variant="body3"
                                          color="text.secondary"
                                        >
                                          {formatDate(insight.created_date)}
                                        </CustomTypography>
                                      </Box>
                                    </Box>
                                  );
                                },
                              )}
                          </Box>
                        </Collapse>
                      </Box>
                    )}

                    <Divider sx={{ my: 4 }} />
                    <Box
                      display="flex"
                      justifyContent="flex-end"
                      alignItems={{ xs: "stretch", sm: "center" }}
                      flexDirection={{ xs: "column", sm: "row" }}
                      gap={{ xs: 2, sm: 0 }}
                    >
                      <Box
                        display="flex"
                        gap={2}
                        flexDirection={{ xs: "column", sm: "row" }}
                      >
                        <AppButton
                          label="Reset"
                          variant="grayscale"
                          onClick={() => resetSourcingSection()}
                          customStyles={{
                            width: { xs: "100%", sm: 120, md: 150 },
                          }}
                        />
                        <AppButton
                          label="Save Sourcing"
                          disabled={
                            !userStore.hasPermission(
                              EmployeeRoles.EDIT_PRODUCT_SOURCING_DETAILS,
                            )
                          }
                          onClick={() => saveSourcingDetails()}
                          customStyles={{
                            width: { xs: "100%", sm: 120, md: 150 },
                            background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 100%)`,
                            color: theme.palette.customColors.white,
                            "&:hover": {
                              background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientHoverStart} 0%, ${theme.palette.customColors.blueGradientHoverEnd} 100%)`,
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  </CardContent>
                </Collapse>
              </Card>
            )}

            {productId && (
              <Card sx={{ marginBottom: 4 }}>
                <CardHeader
                  title={
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <img
                          src={SearchIcon}
                          alt="Search"
                          style={{ width: 30, height: 30, color: "white" }}
                        />
                      </Box>
                      <CustomTypography variant="heading5">
                        SEO Optimization
                      </CustomTypography>
                      <Chip
                        label="Search Engine"
                        size="small"
                        sx={{
                          ml: 1,
                          borderRadius: 2,
                          backgroundColor: theme.palette.customColors.white,
                          color: theme.palette.customColors.greyTextColor,
                          border: `1px solid ${theme.palette.customColors.greyBackground}`,
                          height: "20px",
                          fontSize: "0.65rem",
                          "& .MuiChip-label": {
                            px: 0.8,
                            py: 0,
                          },
                        }}
                      />
                    </Box>
                  }
                  action={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box sx={{ display: { xs: "none", sm: "block" } }}>
                        <CustomTypography
                          variant="body2"
                          color="text.secondary"
                        >
                          2 fields
                        </CustomTypography>
                      </Box>
                      <IconButton>
                        {productStore.openSections.seo ? (
                          <ExpandLess />
                        ) : (
                          <ExpandMore />
                        )}
                      </IconButton>
                    </Box>
                  }
                  sx={{
                    cursor: "pointer",
                    px: 3,
                    py: 2,
                    borderBottom: `1px solid ${theme.palette.customColors.greyBackground}`,
                    "&:hover": {
                      backgroundColor:
                        theme.palette.customColors.greyBackground,
                    },
                  }}
                  onClick={() => productStore.toggleSection("seo")}
                />

                <Collapse
                  in={productStore.openSections.seo}
                  timeout="auto"
                  unmountOnExit
                >
                  <CardContent sx={{ px: 4 }}>
                    <Box>
                      <Box>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          mb={2}
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            <img
                              src={NotesIcon}
                              alt="Search"
                              style={{ width: 17, height: 17 }}
                            />
                            <CustomTypography variant="heading8">
                              SEO Title{" "}
                              <span
                                style={{
                                  color: theme.palette.red.main,
                                }}
                              >
                                *
                              </span>
                            </CustomTypography>
                          </Box>
                          <CustomTypography
                            variant="body2"
                            color={
                              (productStore.productDetails.seo_title?.length ||
                                0) > 60
                                ? "error"
                                : "text.secondary"
                            }
                          >
                            {productStore.productDetails.seo_title?.length || 0}
                            /60
                          </CustomTypography>
                        </Box>
                        <FormTextField
                          multiline
                          placeholder="Enter SEO Title"
                          type="name"
                          disabled={
                            !userStore.hasPermission(
                              EmployeeRoles.EDIT_PRODUCT_SEO_DETAILS,
                            )
                          }
                          value={productStore.productDetails?.seo_title || ""}
                          onChange={(id, value) =>
                            productStore.updateSEOTitle(String(value))
                          }
                          borderColor={
                            theme.palette.customColors.greyBackground
                          }
                          maxLength={60}
                        />
                      </Box>

                      <Box>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          mb={2}
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            <img
                              src={NotesIcon}
                              alt="Search"
                              style={{ width: 17, height: 17 }}
                            />
                            <CustomTypography
                              variant="heading8"
                              fontWeight={600}
                            >
                              Meta Description{" "}
                              <span
                                style={{
                                  color: theme.palette.red.main,
                                }}
                              >
                                *
                              </span>
                            </CustomTypography>
                          </Box>
                          <CustomTypography
                            variant="body2"
                            color={
                              (productStore.productDetails.seo_description
                                ?.length || 0) > 160
                                ? "error"
                                : "text.secondary"
                            }
                          >
                            {productStore.productDetails.seo_description
                              ?.length || 0}
                            /160
                          </CustomTypography>
                        </Box>
                        <FormTextField
                          multiline
                          placeholder="Enter Meta Description"
                          rows={4}
                          type="name"
                          disabled={
                            !userStore.hasPermission(
                              EmployeeRoles.EDIT_PRODUCT_SEO_DETAILS,
                            )
                          }
                          value={
                            productStore.productDetails?.seo_description || ""
                          }
                          onChange={(id, value) =>
                            productStore.updateSEODescription(String(value))
                          }
                          borderColor={
                            theme.palette.customColors.greyBackground
                          }
                          maxLength={160}
                        />
                      </Box>
                    </Box>
                  </CardContent>

                  <CardActions
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: { xs: "stretch", sm: "center" },
                      flexDirection: { xs: "column", sm: "row" },
                      gap: { xs: 2, sm: 0 },
                      px: 4,
                      py: 3,
                      borderTop: `1px solid ${theme.palette.customColors.lightBorder}`,
                    }}
                  >
                    <Box
                      display="flex"
                      gap={2}
                      flexDirection={{ xs: "column", sm: "row" }}
                    >
                      <AppButton
                        label="Reset"
                        variant="grayscale"
                        onClick={() => resetSEOSection()}
                        customStyles={{
                          width: { xs: "100%", sm: 120, md: 150 },
                        }}
                      />
                      <AppButton
                        label="Save SEO"
                        disabled={
                          !userStore.hasPermission(
                            EmployeeRoles.EDIT_PRODUCT_SEO_DETAILS,
                          )
                        }
                        onClick={() => saveSEODetails()}
                        customStyles={{
                          width: { xs: "100%", sm: 120, md: 150 },
                          background: `linear-gradient(90deg, ${theme.palette.customColors.purpleGradientStart} 0%, ${theme.palette.customColors.purpleGradientEnd} 100%)`,
                          color: theme.palette.customColors.white,
                          "&:hover": {
                            background: `linear-gradient(90deg, ${theme.palette.customColors.purpleGradientEnd} 0%, ${theme.palette.customColors.purpleGradientStart} 100%)`,
                          },
                        }}
                      />
                    </Box>
                  </CardActions>
                </Collapse>
              </Card>
            )}
          </>
        )}
      </Box>
      <ActivityLogDrawer
        isOpen={isActivityDrawerOpen}
        onClose={() => setIsActivityDrawerOpen(false)}
        activityLogs={productStore.activityLogs}
        isLoading={isActivityLoading}
        title="Activity History"
        subtitle={productId ? `Product #${productId}` : null}
      />
    </Box>
  );
};

export default observer(AddProduct);
